import { useMemo, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { matchesQuery } from "../../lib/foldText";
import { useSuppliers } from "../../lib/useSuppliers";
import { useAuth } from "../../auth/AuthContext";
import Ic from "../../components/icons.jsx";

/**
 * Supplier codebook ("Dobavljači", sprint 5h): the companies pieces are bought
 * from. The list shows the data that identifies a company at a glance; a row
 * opens the supplier's card, where the data is edited and its change history
 * is shown. The table reads the shared `useSuppliers` cache.
 */
export default function AdminSuppliersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("suppliers:create");
  const { suppliers, loading, error } = useSuppliers();
  const [q, setQ] = useState("");

  const visible = useMemo(
    () => suppliers.filter((s) => matchesQuery(`${s.name} ${s.taxId ?? ""} ${s.registrationNo ?? ""} ${s.city ?? ""} ${s.contactPerson ?? ""}`, q)),
    [suppliers, q],
  );

  const colName = t("suppliers.col.name", "Naziv");
  const colTaxId = t("suppliers.col.taxId", "PIB");
  const colCity = t("suppliers.col.city", "Mesto");
  const colPhone = t("suppliers.col.phone", "Telefon");
  const colContact = t("suppliers.col.contactPerson", "Kontakt osoba");
  const colAssets = t("suppliers.col.assetCount", "Asseta");
  const dash = <span className="muted">—</span>;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("suppliers.title", "Dobavljači")}</h1>
          <div className="sub">{t("suppliers.subtitle", "Šifarnik dobavljača — firme od kojih se delovi nabavljaju. Na assetu se dobavljač bira iz ove liste; karton dobavljača čuva istoriju izmena njegovih podataka.")}</div>
        </div>
        {canCreate && (
          <div className="page-actions">
            <button type="button" className="btn primary" onClick={() => navigate("/admin/dobavljaci/novi")}>
              <Ic.plus /> {t("suppliers.new", "Novi dobavljač")}
            </button>
          </div>
        )}
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          <Ic.search aria-hidden="true" />
          <input
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("suppliers.filter.searchPlaceholder", "Pretraga po nazivu, PIB-u ili mestu…")}
            aria-label={t("action.search", "Pretraga")}
          />
        </div>
        {q !== "" && (
          <button type="button" className="btn sm ghost" onClick={() => setQ("")}>
            {t("action.clear", "Očisti")}
          </button>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <section className="card">
        {loading ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : visible.length === 0 ? (
          <div className="empty">
            {suppliers.length === 0
              ? t("suppliers.empty", "Šifarnik je prazan. Dodajte prvog dobavljača.")
              : t("suppliers.emptyFiltered", "Nijedan dobavljač ne odgovara pretrazi.")}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card">
              <thead>
                <tr>
                  <th>{colName}</th>
                  <th>{colTaxId}</th>
                  <th>{colCity}</th>
                  <th>{colPhone}</th>
                  <th className="hide-tablet">{colContact}</th>
                  <th className="num">{colAssets}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((s) => (
                  <tr
                    key={s.id}
                    className="is-clickable"
                    onClick={() => navigate(`/admin/dobavljaci/${s.id}`)}
                    onKeyDown={(e) => { if (e.key === "Enter") navigate(`/admin/dobavljaci/${s.id}`); }}
                    tabIndex={0}
                    aria-label={t("suppliers.openAria", "Otvori karton dobavljača: {{name}}", { name: s.name })}
                  >
                    <td data-label={colName}><b>{s.name}</b></td>
                    <td className="mono" data-label={colTaxId}>{s.taxId || dash}</td>
                    <td data-label={colCity}>{s.city || dash}</td>
                    <td data-label={colPhone}>{s.phone || dash}</td>
                    <td className="hide-tablet" data-label={colContact}>{s.contactPerson || dash}</td>
                    <td className="num" data-label={colAssets}>{s.assetCount > 0 ? s.assetCount : dash}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
